/**
 * Regras do plano de ensino ("ESTOU APROVADO?"):
 *   MF = (P1 + P2) / 2 ≥ 6 → aprovado;
 *   senão MF = (MAX(P1, P2) + P3) / 2 ≥ 6 → aprovado;
 *   quem fez a P3 e ficou com pelo menos 5,75 tem a nota arredondada para 6;
 *   e sempre frequência ≥ 75%.
 */
import type { Notas } from './types';

export const MEDIA_APROVACAO = 6;
export const MEDIA_ARREDONDA = 5.75;

export type Formula = Record<string, number>;

/** Soma ponderada; null se faltar algum componente. */
export function aplica(formula: Formula, notas: Notas): number | null {
  let total = 0;
  for (const [c, peso] of Object.entries(formula)) {
    const v = notas[c as keyof Notas];
    if (typeof v !== 'number') return null;
    total += peso * v;
  }
  return total;
}

export interface Situacao {
  tipo: 'aprovado' | 'p3' | 'reprovado' | 'andamento';
  mf: number | null;
  arredondada: boolean;
}

export function situacao(n: { P1?: number | null; P2?: number | null; P3?: number | null }): Situacao {
  const { P1, P2, P3 } = n;
  if (typeof P1 !== 'number' || typeof P2 !== 'number') return { tipo: 'andamento', mf: null, arredondada: false };
  const mf1 = (P1 + P2) / 2;
  if (mf1 >= MEDIA_APROVACAO) return { tipo: 'aprovado', mf: mf1, arredondada: false };
  if (typeof P3 !== 'number') return { tipo: 'p3', mf: mf1, arredondada: false };
  const mf2 = (Math.max(P1, P2) + P3) / 2;
  if (mf2 >= MEDIA_APROVACAO) return { tipo: 'aprovado', mf: mf2, arredondada: false };
  if (mf2 >= MEDIA_ARREDONDA) return { tipo: 'aprovado', mf: MEDIA_APROVACAO, arredondada: true };
  return { tipo: 'reprovado', mf: mf2, arredondada: false };
}

/** P2 mínima para fechar com (P1 + P2) / 2 ≥ 6, sem precisar da P3. */
export const p2Necessaria = (p1: number) => Math.max(0, 2 * MEDIA_APROVACAO - p1);

/** P3 mínima, contando o arredondamento de 5,75 para 6 de quem faz a P3. */
export const p3Necessaria = (p1: number, p2: number) => Math.max(0, 2 * MEDIA_ARREDONDA - Math.max(p1, p2));

/**
 * Com os outros dois componentes da P2 fixos, quanto precisa no que falta.
 * Ex.: T2 e GRU2 = 10 → IND2 necessária para P2 = alvo.
 */
export function componenteNecessario(formula: Formula, alvo: number, fixos: Record<string, number>, livre: string) {
  const resto = Object.entries(formula)
    .filter(([c]) => c !== livre)
    .reduce((s, [c, peso]) => s + peso * (fixos[c] ?? 0), 0);
  const peso = formula[livre] ?? 0;
  return peso ? (alvo - resto) / peso : Infinity;
}

export const fmt = (x: number | null | undefined, casas = 2) =>
  typeof x === 'number' && Number.isFinite(x)
    ? x.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
    : '–';

export const fmtPct = (x: number | null | undefined) =>
  typeof x === 'number' ? `${Math.round(x * 100).toLocaleString('pt-BR')}%` : '–';
